import produce from 'immer'
import {
  assign,
  filter,
  get,
  isNil,
  keys,
  map,
  omit,
  pick,
  values,
} from 'lodash'

import { mergeArrayOfObjects } from '../../../core/utils'

import { transformIngredientKeywordToInitialState } from '../../../common/graphql/transformers'
import { flattenTreeStructure } from '../../../common/utils'

import {
  getKeywordsToTransfer,
  moveKeywordsToNodeInSameIngredient,
  transferKeywordsToNodeFromOtherIngredient,
} from '../../helpers'
import {
  CLEAR_TRANSFER_KEYWORD_ERROR,
  TOGGLE_TRANSFER_KEYWORDS_TO_KEYWORD_NODE,
  TRANSFER_KEYWORDS_TO_INGREDIENT_KEYWORD_NODE,
  TRANSFER_KEYWORDS_TO_KEYWORD_NODE,
  TRIGGER_TRANSFER_KEYWORDS_TO_DICTIONARY_TYPE,
  UPDATE_TRANSFERED_KEYWORDS,
} from '../action-types'

const ingredientKeywordsTransferReducer = (state, action) => {
  switch (action.type) {
    case UPDATE_TRANSFERED_KEYWORDS.START:
      return produce(state, nextState => {
        assign(nextState.keywords, {
          transfer: {
            ...nextState.keywords.transfer,
            isLoading: true,
            isFinishedTransfering: false,
            error: undefined,
          },
        })

        return nextState
      })

    case UPDATE_TRANSFERED_KEYWORDS.COMPLETE:
      return produce(state, nextState => {
        const response = !isNil(action.payload.variables.type)
          ? action.payload.data.transferIngredientKeywordsToDictionaryType
          : action.payload.data.updateTransferedKeywords

        const { synonyms, varieties, discarded, uncategorized } =
          transformIngredientKeywordToInitialState(response)

        const newState = {
          isLoading: false,
          error: undefined,
          isDirty: false,
          initialData: {
            synonyms,
            varieties,
            discarded,
            uncategorized,
          },
          data: { synonyms, varieties, discarded, uncategorized },
          checkedKeywordKeys: {
            synonyms: [],
            varieties: [],
            discarded: [],
            uncategorized: [],
          },
        }

        return assign(nextState, {
          data: {
            ...nextState.data,
            ...pick(response, ['updatedAt', 'updatedBy']),
          },
          keywords: {
            main: newState,
            supporting: {
              ...omit(newState, ['initialData', 'isDirty']),
            },
            transfer: {
              keywordsToTransfer: [],
              isFinishedTransfering: false,
            },
          },
        })
      })

    case UPDATE_TRANSFERED_KEYWORDS.FAIL:
      return produce(state, nextState => {
        assign(nextState.keywords, {
          transfer: {
            ...nextState.keywords.transfer,
            keywordsToTransfer: [],
            isFinishedTransfering: false,
            isLoading: false,
            error: action.error.message,
          },
        })

        return nextState
      })

    case TOGGLE_TRANSFER_KEYWORDS_TO_KEYWORD_NODE:
      return produce(state, nextState => {
        const { synonyms, varieties, discarded, uncategorized } =
          nextState.keywords.main.data

        assign(nextState.keywords, {
          supporting: {
            isLoading: false,
            error: undefined,
            data: { synonyms, varieties, discarded, uncategorized },
            checkedKeywordKeys: {
              synonyms: [],
              varieties: [],
              discarded: [],
              uncategorized: [],
            },
          },
        })

        return nextState
      })

    case TRIGGER_TRANSFER_KEYWORDS_TO_DICTIONARY_TYPE:
      return produce(state, nextState => {
        const mainKeywordsState = nextState.keywords.main

        const flattenMainKeywordsGroupedByField = mergeArrayOfObjects(
          map(keys(mainKeywordsState.data), keywordType => ({
            [keywordType]: flattenTreeStructure(
              get(mainKeywordsState.data, keywordType),
            ),
          })),
        )

        const keywordsToTransfer = getKeywordsToTransfer(
          mainKeywordsState,
          flattenMainKeywordsGroupedByField,
        )

        const keywordsToTransferWithoutSubtypeNodes = filter(
          values(keywordsToTransfer),
          ({ isSubtype }) => !isSubtype,
        )

        assign(nextState.keywords, {
          transfer: {
            ...nextState.keywords.transfer,
            keywordsToTransfer: keywordsToTransferWithoutSubtypeNodes,
            isFinishedTransfering: true,
          },
        })

        return nextState
      })
    case TRANSFER_KEYWORDS_TO_INGREDIENT_KEYWORD_NODE:
      return produce(state, nextState => {
        try {
          const keywordsToTransfer = transferKeywordsToNodeFromOtherIngredient(
            nextState.keywords.main,
            nextState.keywords.supporting,
          )

          assign(nextState.keywords, {
            transfer: {
              ...nextState.keywords.transfer,
              keywordsToTransfer: filter(
                values(keywordsToTransfer),
                ({ isSubtype }) => !isSubtype,
              ),
              isFinishedTransfering: true,
            },
            supporting: {
              ...nextState.keywords.supporting,
              checkedKeywordKeys: {
                synonyms: [],
                varieties: [],
                discarded: [],
                uncategorized: [],
              },
            },
          })
        } catch (error) {
          assign(nextState.keywords, {
            transfer: {
              ...nextState.keywords.transfer,
              error: error.message,
              isFinishedTransfering: false,
            },
          })
        }

        return nextState
      })

    case TRANSFER_KEYWORDS_TO_KEYWORD_NODE:
      return produce(state, nextState => {
        try {
          const newKeywordTrees = moveKeywordsToNodeInSameIngredient(
            nextState.keywords.main,
            nextState.keywords.supporting,
          )

          assign(nextState.keywords, {
            main: {
              ...nextState.keywords.main,
              data: newKeywordTrees,
              isDirty: true,
              checkedKeywordKeys: {
                synonyms: [],
                varieties: [],
                discarded: [],
                uncategorized: [],
              },
            },
          })
        } catch (error) {
          assign(nextState.keywords, {
            main: {
              ...nextState.keywords.main,
              error: { message: error.message },
            },
          })
        }

        return nextState
      })

    case CLEAR_TRANSFER_KEYWORD_ERROR:
      return produce(state, nextState => {
        assign(nextState.keywords, {
          transfer: {
            ...nextState.keywords.transfer,
            error: undefined,
          },
        })

        return nextState
      })
    default:
      return state
  }
}

export default ingredientKeywordsTransferReducer
